import { storeCategories$ } from '@psl/functions/firebase/observables/storeCategories$';
import { WithID } from '@psl/functions/firebase/rxfireExtensions';
import { useObservable } from '@psl/functions/hooks/useObservable';
import { isSeller } from '@psl/functions/models/profiles';
import { CategorizedStore, Profile } from '@psl/models/ClientModels';
import { memoize } from '@psl/utils/memoize';
import { onErrorIgnore } from '@psl/utils/onErrorIgnore';
import { useEffect, useState } from 'react';
import { firstValueFrom, of } from 'rxjs';

export const storeCategoriesMemo = memoize((uid: string) =>
  firstValueFrom(storeCategories$(uid)),
);

/*
 There will be cache problems. If the user switches from an ordinary user to a seller, there will be problems with the return value here
 The judgment condition needs to be set in the key
*/
export const useStoreCategories = (
  uid: string | null | undefined,
  profile: WithID<Profile> | null | undefined,
) => {
  const isSellerProfile = uid && profile && isSeller(profile);
  return useObservable(
    isSellerProfile ? storeCategories$(uid) : of([]),
    `stores/${uid}-${isSellerProfile}/categories`,
  );
};

export const useStoreCategoriesLazily = (
  uid: string | null | undefined,
  profile: WithID<Profile> | null | undefined,
) => {
  const isSellerProfile = uid && profile && isSeller(profile);
  const [categories, setCategories] = useState<WithID<CategorizedStore>[]>();

  useEffect(() => {
    if (!isSellerProfile) return;
    storeCategoriesMemo(uid).then(setCategories).catch(onErrorIgnore);
  }, [isSellerProfile, uid]);

  return isSellerProfile ? categories : null;
};
